import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle, FaSyringe } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { DetailedCitizenVaccinationsIndicatorC6Data } from "@/services/esus/indicator/esf/c6/getDetailedCitizensVaccinationsIndicatorC6";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";
import { IndicatorEsfStatusEnum } from "@/utils/enums/IndicatorEsfStatusEnum";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC6CitizensVaccinationsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	influenzaVaccination: Column;
};

type IndicatorEsfC6CitizensVaccinationsTableProps = {
	citizensVaccinations: DetailedCitizenVaccinationsIndicatorC6Data[];
	isLoading: boolean;
	openCitizenHistory: (
		citizenWithVaccinations: DetailedCitizenVaccinationsIndicatorC6Data
	) => void;
};

export function getCitizenVaccinationStatusDescription(
	citizenVaccinationStatus?: IndicatorEsfStatusEnum
) {
	switch (citizenVaccinationStatus) {
		case IndicatorEsfStatusEnum.Finished:
			return "Realizado";
		case IndicatorEsfStatusEnum.Pending:
			return "Pendente";
		default:
			return "Fora do prazo";
	}
}

function getIconCitizenVaccinationStatus(citizenVaccinationStatus?: IndicatorEsfStatusEnum) {
	const title = getCitizenVaccinationStatusDescription(citizenVaccinationStatus);
	switch (citizenVaccinationStatus) {
		case IndicatorEsfStatusEnum.Finished:
			return <FaSyringe title={title} className="text-primary fs-2" />;
		case IndicatorEsfStatusEnum.Pending:
			return <FaSyringe title={title} className="text-warning fs-2" />;
		default:
			return <FaSyringe title={title} className="text-muted fs-2" />;
	}
}

export function IndicatorEsfC6CitizensVaccinationsTable({
	citizensVaccinations,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC6CitizensVaccinationsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC6CitizensVaccinationsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		influenzaVaccination: { title: "Influenza", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.influenzaVaccination.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.influenzaVaccination.title}
							<CardOverlayTrigger
								header="Influenza"
								message="Aplicações do imunobiológico em idosos"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC6CitizensVaccinationsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC6CitizensVaccinationsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC6CitizensVaccinationsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensVaccinations.length > 0
					? citizensVaccinations.map((citizenWithVaccinations, index) => (
							<tr key={`citizen-vaccinations-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.fullName}</div>
										<div className="text-muted">
											{citizenWithVaccinations.birthdate
												? dayjs(citizenWithVaccinations.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithVaccinations.birthdate)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.cpf
										? formatIdentifier(citizenWithVaccinations.cpf)
										: citizenWithVaccinations.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithVaccinations.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithVaccinations.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithVaccinations.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.influenzaVaccination.visible && "d-none"
									}`}
								>
									{getIconCitizenVaccinationStatus(
										citizenWithVaccinations.vaccinationStatus
									)}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithVaccinations)}
										title="Histórico de vacinas do idoso"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
